import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { isDesktopApp } from '@frontend/utils/desktop';

/**
 * In the Opal desktop (Electron) shell, an internal link opened via
 * window.open / target="_blank" (or a full-page navigation to an in-app route)
 * is intercepted in the main process and forwarded here instead of reloading
 * the whole app. This hook subscribes to those requests and performs a
 * client-side react-router navigation, so the SPA moves without a full reload
 * (no white flash, no bundle re-download, no lost state).
 *
 * No-op in the web build and on desktop shells that predate the bridge
 * (`onNavigate` absent) — in the latter, the shell falls back to a full load.
 * Mount once inside the router (e.g. the root app component).
 */
export function useDesktopNavigation(): void {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isDesktopApp()) return;
    const onNavigate = window.desktopAPI?.onNavigate;
    if (!onNavigate) return;

    return onNavigate((rawUrl: string) => {
      try {
        const { pathname, search, hash } = new URL(rawUrl);
        // Only the in-app path — the shell guarantees same-origin before
        // emitting, so origin is dropped and react-router owns the route.
        navigate(`${pathname}${search}${hash}`);
      } catch {
        // Malformed URL — ignore; the shell's timeout falls back to a load.
      }
    });
  }, [navigate]);
}
